// base
import React, { useRef, useMemo } from 'react';

// libraries
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  type ChartOptions
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export interface BarChartDataSetProps {
  label: string;
  data: number[];
  backgroundColor: string;
}

interface BarChartProps {
  labels: string[];
  customOptions?: ChartOptions<'bar'>;
  dataSets: BarChartDataSetProps[];
  canvasHeight?: number;
}

export const BarChart: React.FC<BarChartProps> = ({
  labels,
  dataSets,
  customOptions,
  canvasHeight = 200
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false,
            color: '#2222224D'
          },
          ticks: {
            color: '#222222'
          }
        },
        y: {
          grid: {
            display: true,
            color: '#2222224D',
            borderDash: [6, 4],
            lineWidth: 1
          },
          ticks: {
            color: '#000',
            stepSize: 50
          },
          position: 'right',
          min: 0
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#ffffff',
          borderWidth: 1
        }
      },
      ...customOptions
    }),
    [customOptions, labels]
  );

  const chartData = useMemo(
    () => ({
      labels: labels,
      datasets: dataSets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: dataset.backgroundColor,
        borderRadius: 0,
        borderSkipped: false,
        barThickness: 20,
        maxBarThickness: 30
      }))
    }),
    [labels, dataSets]
  );

  return (
    <Bar
      ref={chartRef}
      data={chartData}
      options={options}
      updateMode="resize"
      height={canvasHeight}
    />
  );
};
